import type { ReactNode } from 'react';

/**
 * Canonical outer layout shell for the rule-16 3-section response skeleton
 * (ticket #101/WS-5). Pure presentation: Message.tsx (and FinalResponseRenderer
 * upstream of it) keep ownership of WHAT data renders in each slot; this
 * component owns only WHERE it lands and in what order, so every response
 * family (chat, admin trace replay, terminal/clarification) assembles chips,
 * the references panel, and the meta footer through the same one mount point
 * instead of duplicating that JSX ad hoc at each call site.
 *
 * Slots map directly onto the 3-section skeleton:
 *  - s1Text: Section 1 summary lead-in (plain text, no card).
 *  - children: Section 2 content card(s) (e.g. S2Card-based content, or a
 *    markdown-only fallback body for non-structured answers).
 *  - references: Clinical references sub-panel, one mount point.
 *  - chips: Section 3 "Suggested follow-up" chips.
 *  - footer: meta footer row (elapsed time / route label / source links),
 *    rendered below Section 3, outside its border.
 */
export function ResponseFrame({
  s1Text,
  children,
  references,
  chips,
  footer,
}: {
  s1Text?: ReactNode;
  children?: ReactNode;
  references?: ReactNode;
  chips?: ReactNode;
  footer?: ReactNode;
}) {
  const hasAny = Boolean(s1Text || children || references || chips || footer);
  if (!hasAny) return null;

  return (
    <div className="response-frame space-y-4" data-testid="response-frame">
      {s1Text ? <div data-testid="response-frame-s1">{s1Text}</div> : null}
      {children ? <div data-testid="response-frame-s2">{children}</div> : null}
      {references ? <div data-testid="response-frame-references" className="mt-4">{references}</div> : null}
      {chips ? <div data-testid="response-frame-chips">{chips}</div> : null}
      {footer ? <div data-testid="response-frame-footer">{footer}</div> : null}
    </div>
  );
}
